import React, { useState, useEffect } from 'react';
import { FaBars, FaLock, FaSignOutAlt, FaTimes, FaBell } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import PasswordInput from '../../components/Input/PasswordInput';
import { changePasswordAPI, getCurrentUser, logout, getAllNotificationsAPI, markNotificationAsReadAPI } from '../../utils/api';
import { setNotificationCount } from '../../store/slices/countState';
import Toast from '../../components/Toast/Toast';
import './Header.css';

const Header = ({ onMenuToggle, isMenuOpen, title = "Dashboard" }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const notificationCount = useSelector(state => state.countState.notificationCount);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    old_password: '',
    password: '',
    confirm_password: ''
  });
  const [toast, setToast] = useState({ show: false, type: '', message: '' });

  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => {
        setToast({ show: false, type: '', message: '' });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  const fetchNotificationCount = async () => {
    try {
      const response = await getAllNotificationsAPI();
      
      if (response?.status === 200 && response.data) {
        const unreadCount = response.data.filter(notification => notification?.read === 0).length;
        dispatch(setNotificationCount(unreadCount));
      }
    } catch (error) {
      console.log('Error fetching notification count:', error);
    }
  };

  useEffect(() => {
    fetchNotificationCount();
  }, []);

  const handleBellClick = async () => {
    try {
      await markNotificationAsReadAPI();
      
      await fetchNotificationCount();
      navigate('/notifications');
    } catch (error) {
      console.log('Error marking notifications as read:', error);
      navigate('/notifications');
    }
  };

  const currentUser = getCurrentUser();
  const userName = currentUser?.name || 'Yug Mantra';
  const userEmail = currentUser?.email || 'admin@gmail.com';
  const firstLetter = userName.charAt(0).toUpperCase();

  const handleProfileUpdate = async (e) => {
    e.preventDefault();

    if (profileData.password !== profileData.confirm_password) {
      setToast({ show: true, type: 'error', message: 'Passwords do not match!' });
      return;
    }

    if (profileData.password.length < 6) {
      setToast({ show: true, type: 'error', message: 'Password must be at least 6 characters long!' });
      return;
    }

    setLoading(true);
    try {
      const reqData = {
        user_id: currentUser?._id,
        ...profileData
      };

      const response = await changePasswordAPI(reqData);
      if (response.status === 200) {
        setShowPasswordModal(false);
        setProfileData({ old_password: '', password: '', confirm_password: '' });
        setToast({ show: true, type: 'success', message: 'Password changed successfully' });
      } else {
        setToast({ show: true, type: 'error', message: response.message || 'Failed to change password!' });
      }
    } catch (error) {
      console.log('Password change error:', error);
      setToast({ show: true, type: 'error', message: 'An error occurred while changing password. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <header className={`header ${isMenuOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        <div className="header-left">
          <button
            type="button"
            className="menu-toggle"
            onClick={onMenuToggle}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
          <h1 className="dashboard-title">{title}</h1>
        </div>

        <div className="header-right">
          <div className="notification-icon" onClick={handleBellClick} title="Notifications">
            <FaBell />
            {notificationCount > 0 && <span className="notification-dot"></span>}
          </div>
          <div className="user-avatar" onClick={() => setShowProfileModal(true)}>
            <span className="avatar-text">{firstLetter}</span>
          </div>
        </div>
      </header>

      {/* Profile Dropdown Modal */}
      {showProfileModal && (
        <div className="modal-overlay" onClick={() => setShowProfileModal(false)}>
          <div className="profile-dropdown" onClick={(e) => e.stopPropagation()}>
            <div className="profile-header">
              <div className="profile-avatar">
                <span className="profile-avatar-text">{firstLetter}</span>
              </div>
              <div className="profile-info">
                <h3 className="profile-name">{userName}</h3>
                <p className="profile-email">{userEmail}</p>
              </div>
            </div>

            <div className="profile-separator"></div>

            <div className="profile-actions">
              <button
                type="button"
                className="profile-action-btn"
                onClick={() => {
                  setShowProfileModal(false);
                  setShowPasswordModal(true);
                }}
                disabled={loading}
              >
                <FaLock className="action-icon-header" />
                <span>Change Password</span>
              </button>
              <button
                type="button"
                className="profile-action-btn"
                onClick={logout}
              >
                <FaSignOutAlt className="action-icon-header" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {showPasswordModal && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Change Password</h3>
              <button
                type="button"
                className="modal-close"
                onClick={() => setShowPasswordModal(false)}
                disabled={loading}
                aria-label="Close"
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleProfileUpdate} className="header-profile-form">
              <PasswordInput
                title="Old Password"
                value={profileData.old_password}
                onChange={(e) => setProfileData({ ...profileData, old_password: e.target.value })}
                required
                disabled={loading}
                placeholder="Enter your current password"
              />

              <PasswordInput
                title="New Password"
                value={profileData.password}
                onChange={(e) => setProfileData({ ...profileData, password: e.target.value })}
                required
                disabled={loading}
                placeholder="Enter new password (min 6 characters)"
                minLength={6}
              />

              <PasswordInput
                title="Confirm Password"
                value={profileData.confirm_password}
                onChange={(e) => setProfileData({ ...profileData, confirm_password: e.target.value })}
                required
                disabled={loading}
                placeholder="Confirm your new password"
              />

              <div className="form-actions">
                <button type="submit" disabled={loading} aria-busy={loading}>
                  Change Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <Toast toast={toast} />
    </>
  );
};

export default Header; 